// Render-agnostic S-13 (Territory Assignment Record) data model, shared by the PDF
// (components/congregation/territory/pdf.ts) and DOCX (helpers/docx_s13.ts) renderers.
// Nothing here should import jspdf or docx - that's what keeps each renderer's bundle
// free of the other's dependency.
import dayjs from "dayjs";
import { dateStringCompare, serviceYearStart, stringToLocaleDate } from "./dateHelpers";
import { territoryCompare } from "../components/congregation/territory/common";
import { Territory, TerritoryRecord } from "../types/scheduling/territory";
import User from "../types/user";

export const S13_ROWS_PER_PAGE = 20;
export const S13_RECORDS_PER_ROW = 4;
export const S13_NO_USER = "---";

export type S13Cell = { assignedTo: string; dateAssigned: string; dateCompleted: string };
export type S13Row = { terrNumber: string; terrLocality: string; lastWorked: string; cells: S13Cell[] };
export type S13Page = { serviceYear: number; business: boolean; rows: S13Row[] };

type TerrChunk = { terr: Territory; lastWorked: string; records: TerritoryRecord[] };

export function getRecordsForServiceYear(
  territories: Territory[],
  territoryRecords: TerritoryRecord[],
  yearStart: dayjs.Dayjs,
  summary: boolean,
): TerrChunk[] {
  const yearEnd = yearStart.add(1, "year");

  return territories.sort(territoryCompare).flatMap((terr) => {
    const terrRecs = territoryRecords
      .filter((tr) => tr.territory_id === terr.id)
      .sort((a, b) => dateStringCompare(a.start!, b.start!));
    // by using !isBefore, we get on-or-after. if we just use isAfter, then it doesn't account for records
    // checked out on September 1, when the service year starts.
    const validRecs = terrRecs.filter(
      (r) => !dayjs(r.start).isBefore(yearStart, "day") && dayjs(r.start).isBefore(yearEnd, "day"),
    );

    const recordChunks = summary
      ? [validRecs.slice(-4)]
      : Array(Math.ceil(validRecs.length / S13_RECORDS_PER_ROW))
          .fill(0)
          .map((_, i) => validRecs.slice(i * S13_RECORDS_PER_ROW, i * S13_RECORDS_PER_ROW + S13_RECORDS_PER_ROW));

    // lastShownEnd is the earliest end date in recordChunks.
    const lastShownEnd = recordChunks.flat().filter((r) => !!r.end)[0]?.end;
    // lastWorkedBefore is the date that the lastWorked must be before. It represents the earliest end date
    // in recordChunks. If undefined, there were no end dates in recordChunks, so set it to the end of the service year
    const lastWorkedBefore = lastShownEnd ? dayjs(lastShownEnd) : yearEnd;
    // lastWorked is the latest end date of a territory record prior to the ones in recordChunks (if any)
    const lastWorked =
      terrRecs
        .filter((r) => !!r.end) // only records with an end date
        .sort((a, b) => dateStringCompare(b.end!, a.end!)) // reverse sort
        // get the first one before the lastWorkedBefore date
        .find((r) => dayjs(r.end).isBefore(lastWorkedBefore))?.end ?? "";

    if (!recordChunks.length) {
      return [{ terr, lastWorked, records: [] }];
    }

    return recordChunks.map((records) => ({ terr, lastWorked, records }));
  });
}

function buildRow(chunk: TerrChunk | undefined, userMap: Map<number, User>): S13Row {
  const records = chunk?.records ?? [];
  const cells: S13Cell[] = Array.from({ length: S13_RECORDS_PER_ROW }, (_, i) => {
    const rec = records[i];
    return {
      // a record with no user, or a user we can't find, gets a placeholder rather than a blank
      assignedTo: rec ? (userMap.get(rec.user_id ?? 0)?.displayName ?? S13_NO_USER) : "",
      dateAssigned: stringToLocaleDate(rec?.start ?? undefined) || "",
      dateCompleted: stringToLocaleDate(rec?.end ?? undefined) || "",
    };
  });
  return {
    terrNumber: chunk?.terr.number.trim() ?? "",
    terrLocality: chunk?.terr.locality.trim() ?? "",
    lastWorked: stringToLocaleDate(chunk?.lastWorked || undefined) || "",
    cells,
  };
}

export function buildS13Pages(
  territories: Territory[],
  territoryRecords: TerritoryRecord[],
  userMap: Map<number, User>,
  years: number[],
  summary: boolean,
): S13Page[] {
  const pages: S13Page[] = [];

  years.forEach((year) => {
    const yearStart = dayjs(serviceYearStart(new Date(year, 2, 1)));
    const serviceYearRecords = getRecordsForServiceYear(territories, territoryRecords, yearStart, summary);
    const residentialRecords = serviceYearRecords.filter((r) => !r.terr.business);
    const businessRecords = serviceYearRecords.filter((r) => r.terr.business);

    [residentialRecords, businessRecords].forEach((records) => {
      if (!records.length) return;

      Array(Math.ceil(records.length / S13_ROWS_PER_PAGE))
        .fill(0)
        .map((_, i) => records.slice(i * S13_ROWS_PER_PAGE, i * S13_ROWS_PER_PAGE + S13_ROWS_PER_PAGE))
        .forEach((pageRecords) => {
          const business = !!pageRecords[0]?.terr.business;
          const rows: S13Row[] = Array.from({ length: S13_ROWS_PER_PAGE }, (_, i) =>
            buildRow(pageRecords.at(i), userMap),
          );
          pages.push({ serviceYear: year, business, rows });
        });
    });
  });

  return pages;
}
